import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

function useIsMountedRef() {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  });
  return ref;
}

function useStateRef(init) {
  const [state, setState] = useState(init);
  const stateRef = useRef(init);
  const isMounted = useIsMountedRef();
  const setStateWrapper = useCallback((fnOrValue) => {
    function wrap(existing) {
      if (fnOrValue instanceof Function) {
        stateRef.current = fnOrValue(existing);
      } else {
        stateRef.current = fnOrValue;
      }
      return stateRef.current;
    }
    if (!isMounted.current)
      return;
    setState(wrap);
  }, []);
  const ret = [state, setStateWrapper, stateRef];
  return ret;
}

const ASYNC_INIT = {
  finished: false,
  pending: false,
  error: null
};
const ASYNC_PENDING = {
  finished: false,
  pending: true,
  error: null
};
const ASYNC_SUCCESS = {
  finished: true,
  pending: false,
  error: null
};
function useAsyncProcess({ allowMultiple = false } = {}) {
  const isMounted = useIsMountedRef();
  const [state, setState] = useState(ASYNC_INIT);
  const fnRef = useRef(null);
  const run = useCallback(async (fn) => {
    if (fnRef.current && !allowMultiple)
      return;
    fnRef.current = fn;
    try {
      setState(() => ASYNC_PENDING);
      await fn();
      if (fnRef.current === fn && isMounted.current)
        setState(() => ASYNC_SUCCESS);
    } catch (err) {
      const error = err instanceof Object ? err : new Error(`${err}`);
      if (fnRef.current === fn && isMounted.current) {
        setState(() => ({
          finished: true,
          pending: false,
          error
        }));
      }
    } finally {
      if (fnRef.current === fn) {
        fnRef.current = null;
      }
    }
  }, []);
  const reset = useCallback(() => setState(ASYNC_INIT), []);
  return [run, state, reset];
}

function useAsyncValue(fn, defVal, opts) {
  const { resetOnChange = false } = opts || {};
  const [value, setValue, valueRef] = useStateRef(defVal);
  const [run, state] = useAsyncProcess({ allowMultiple: true });
  const fnRef = useRef(null);
  fnRef.current = fn;
  useEffect(() => {
    if (resetOnChange)
      setValue(defVal);
    run(async () => {
      const res = await fn();
      if (fn === fnRef.current)
        setValue(res);
    });
  }, [fn, run]);
  return [value, state, valueRef, setValue];
}

function useAsyncPoll(fn, defVal, pollInterval = 5e3) {
  const [counter, setCounter] = useState(0);
  const cb = useCallback(() => fn(), [fn, counter]);
  useEffect(() => {
    const interval = setInterval(() => setCounter((x) => x + 1), pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval]);
  const [value, state, valRef] = useAsyncValue(cb, defVal);
  const [currentState, setCurrentState] = useState(state);
  useEffect(() => {
    if (currentState.finished) {
      if (state.error || state.finished) {
        setCurrentState(state);
      }
    } else {
      setCurrentState(state);
    }
  }, [state]);
  return [value, currentState, valRef];
}

function combineTwoStatuses(a, b) {
  const error = a.error || b.error;
  const pending = a.pending || b.pending;
  const finished = !error && a.finished && b.finished;
  if (finished) {
    return ASYNC_SUCCESS;
  }
  if (error) {
    return {
      finished: true,
      pending: false,
      error
    };
  }
  return {
    error,
    pending,
    finished
  };
}
function combineStatuses(statuses) {
  if (statuses.length <= 0)
    return ASYNC_INIT;
  return statuses.reduce((res, x) => combineTwoStatuses(res, x), statuses[0]);
}
function useAsyncStatuses(statuses) {
  return useMemo(() => combineStatuses(statuses), [statuses]);
}

function useSubscribedValue(fn, defaultValue) {
  const [state, setState] = useState({
    finished: false,
    pending: true,
    error: null
  });
  const [value, setValue, valueRef] = useStateRef(defaultValue);
  const countRef = useRef(0);
  useEffect(() => {
    var _a;
    countRef.current += 1;
    const cbVersion = countRef.current;
    return (_a = fn && fn((newVal) => {
      if (cbVersion !== countRef.current)
        return;
      setValue(newVal);
      setState({ finished: true, pending: false, error: null });
    })) != null ? _a : void 0;
  }, [fn]);
  return [value, state, valueRef];
}

export { useAsyncPoll, useAsyncProcess, useAsyncStatuses, useAsyncValue, useIsMountedRef, useStateRef, useSubscribedValue };
